/**
 * Pure bits of the AI New Site wizard (AB#642) — the first message Baxter gets,
 * and the model value it opens on.
 */

import type { AiModel } from '../api/aiModels';


/** Baxter Auto — DeepSeek V4.1 Flash, Claude Sonnet 5.5 or Opus 5.5 per message, on WordPuppi's key
 * unless the person has their own OpenRouter key. The id is historical (it was
 * the free router); the wizards open on it. */
export const FREE_MODEL = 'openrouter:openrouter/free';

/** The picker's value for a model row (same shape as BaxterPanel's `modelValue`). */
export const modelValue = (m: AiModel) => `${m.provider}:${m.id}`;

/** Remove only a repeated vendor prefix; keep model/version/context qualifiers intact. */
export const modelDisplayName = (m: Pick<AiModel, 'name'>) => m.name.replace(/^([a-z][a-z0-9 ._-]*):\s*(?=\1(?:\s|[-/]))/i, '');

/** Provider half of a `provider:model` value. */
export function modelProvider(value: string): string {
  const sep = value.indexOf(':');
  return sep > 0 ? value.slice(0, sep) : value;
}

/** Reuse catalog capability ratings; ties stay stable except Rick's named Astra preference. */
export function orderedModels(models: AiModel[]): AiModel[] {
  return [...models].sort((a, b) => (b.strength ?? -1) - (a.strength ?? -1)
    || Number(b.id.split('/').at(-1) === 'gpt-6-astra') - Number(a.id.split('/').at(-1) === 'gpt-6-astra'));
}

/** The wizard's first user turn, handed to Baxter through `baxterBrief`. */
export function buildBrief({
  name,
  description,
  media,
  url,
  answers = [],
}: {
  name: string;
  description: string;
  media: Array<{ filename: string; url: string }>;
  /** Port mode: the preview link Baxter rebuilds here. */
  url?: string;
  /** The wizard's interview, so Baxter plans instead of asking again. */
  answers?: Array<{ question: string; answer: string }>;
}): string {
  const parts = [
    url?.trim()
      ? `Bring over the site at ${url.trim()} — rebuild it here as "${name.trim()}" with its pages, copy, colours and a matching theme.`
      : `Build my site "${name.trim()}".`,
  ];
  const desc = description.trim();
  if (desc) parts.push(desc);
  if (media.length) {
    const list = media.map((m) => `${m.filename} (${m.url})`).join(', ');
    parts.push(`I uploaded these files (already in the media library): ${list}.`);
  }
  if (answers.length) {
    parts.push(`My answers to your questions:\n${answers
      .map((a) => `- ${a.question} ${a.answer.trim() || '(skipped — your call)'}`)
      .join('\n')}`);
  }
  return parts.join('\n\n');
}

export interface InterviewQuestion {
  id: string;
  question: string;
  placeholder: string;
  /** Quick picks; tapping one adds it to the answer. */
  picks?: string[];
  /** Picks selected before the person touches anything. */
  defaults?: string[];
}

const PAGES: InterviewQuestion = {
  id: 'pages',
  question: 'Which pages do you need?',
  placeholder: 'Anything else, or a page to leave out',
  picks: ['Home', 'About', 'Services', 'Work', 'Shop', 'Blog', 'FAQ', 'Contact'],
  defaults: ['Home', 'About', 'Contact'],
};
const STYLE: InterviewQuestion = {
  id: 'style',
  question: 'Any colours, photos or fonts to use?',
  placeholder: 'Hex codes, a font, a site you like — or leave it to Baxter',
  picks: ['Let Baxter choose'],
};
const ACTION: InterviewQuestion = {
  id: 'action',
  question: 'What should a visitor do first?',
  placeholder: 'e.g. book a lesson, ask for a quote',
  picks: ['Book', 'Buy', 'Get in touch', 'Sign up', 'Read and learn'],
};

/** The new-site interview per skill, one question at a time (mirrors each
 * skill's "First turn" list in wpp-core/skills). Port has none: it runs in one go. */
export const SITE_INTERVIEWS: Record<string, InterviewQuestion[]> = {
  love: [
    { id: 'purpose', question: 'What does it do, in your words?', placeholder: 'e.g. We teach new drivers to feel safe on Swedish roads' },
    ACTION,
    { id: 'audience', question: 'Who is it mainly for?', placeholder: 'e.g. new drivers and their parents' },
    PAGES,
    {
      id: 'tone', question: 'How should it sound, and in which language?', placeholder: 'e.g. calm and friendly, in Swedish',
      picks: ['Warm', 'Calm', 'Trustworthy', 'Direct', 'Playful', 'Bold'],
    },
    STYLE,
    { id: 'must', question: 'Anything it must include?', placeholder: 'A section, a line you always say…' },
  ],
  based: [
    {
      id: 'kinds', question: 'What kinds of things is the site about?', placeholder: 'e.g. courses and instructors',
      picks: ['Services', 'Projects', 'Products', 'Team', 'Events', 'Menu', 'Testimonials'],
    },
    {
      id: 'details', question: 'What should a visitor see for each one?', placeholder: 'Anything else that matters',
      picks: ['Price', 'Duration', 'Date', 'Location', 'Photo', 'Short blurb'],
    },
    PAGES,
    ACTION,
    { id: 'tone', question: 'What tone should it have?', placeholder: 'e.g. plain and trustworthy' },
    STYLE,
  ],
  vee: [
    { id: 'refs', question: 'Which sites or brands do you like the look of?', placeholder: 'A link or a name, and what you like about it' },
    {
      id: 'mood', question: 'What mood, in three words?', placeholder: 'And anything to avoid',
      picks: ['Calm', 'Bold', 'Playful', 'Luxurious', 'Plain-spoken'],
    },
    {
      id: 'colour', question: 'Any colour in mind?', placeholder: 'A brand colour or a favourite',
      picks: ['Light', 'Dark', 'You choose'],
    },
    {
      id: 'type', question: 'What should the lettering feel like?', placeholder: 'Or a font you already use',
      picks: ['Classic serif', 'Clean sans', 'Something with character'],
    },
    {
      id: 'opening', question: 'What should the first screen show?', placeholder: 'And the pages you need beyond it',
      picks: ['A big statement', 'A photograph', 'My work up front'],
    },
  ],
  bolt: [ACTION, PAGES, { id: 'tone', question: 'A tone, in a couple of words?', placeholder: 'e.g. friendly, direct' }, STYLE],
};
